<template>
  <v-app>
    <v-layout>
      <v-main app>
        <v-app-bar app>
          <v-app-bar-title>Jellybeans Settings </v-app-bar-title>
        </v-app-bar>
        <!-- Account not set UI - opens dialog -->
        <v-container v-if="settings.accountId === ''">
          <v-dialog v-model="showAccountDialog" persistent max-width="500px">
            <v-card>
              <v-card-title>Set Account ID</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="inputAccountId"
                  label="Account ID"
                  hint="Please enter your account ID"
                  persistent-hint
                  :error-messages="accountIdError"
                  @keyup.enter="setAccountId"
                ></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  @click="setAccountId"
                  :disabled="!inputAccountId || !!accountIdError"
                >
                  Set Account ID
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-container>
        <!-- account not set -->
        <v-container v-if="settings.accountId !== '' && !settings.error">
          <template v-if="settings.placements || settings.loading">
            <!-- Tabs -->
            <v-tabs
              v-model="currentTab"
              color="primary"
              class="d-flex justify-space-around"
            >
              <v-tab value="placements">
                <v-icon start class="mr-sm-2">mdi-view-dashboard</v-icon>
                <span class="d-none d-md-block">Placements</span>
              </v-tab>
              <v-tab value="models">
                <v-icon start class="mr-sm-2">mdi-view-module</v-icon>
                <span class="d-none d-md-block">Models</span>
              </v-tab>
              <!-- <v-tab value="trends">
                <v-icon start class="mr-sm-2">mdi-chart-areaspline</v-icon>
                <span class="d-none d-md-block">Trends</span>
              </v-tab> -->
              <v-tab value="help">
                <v-icon start class="mr-sm-2">mdi-help-circle</v-icon>
                <span class="d-none d-md-block">Help</span>
              </v-tab>
            </v-tabs>
            <!-- Add Placement Button -->
            <div
              class="d-flex ga-2 justify-end mt-6 mt-md-0 w-100"
              v-if="!selectedPlacementId && currentTab === 'placements'"
            >
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="showAddDialog = true"
              >
                Add New
              </v-btn>
            </div>
            <!-- Dashboards -->
            <v-window v-model="currentTab" :touch="false">
              <!-- Placement dashboard -->
              <v-window-item value="placements">
                <v-card>
                  <Placements
                    v-if="selectedPlacementId == null"
                    :account-id="settings.accountId"
                    :loading="settings.loading"
                    :placements="
                      settings?.placements
                        ? Object.keys(settings?.placements?.placements)
                        : []
                    "
                    @update:placementId="selectedPlacementId = $event"
                  />
                  <!-- <Placement settings form /> -->
                  <div v-if="selectedPlacementId != null">
                    <placement-settings-manager
                      :selected-placement-id="selectedPlacementId"
                      @go-back="selectedPlacementId = null"
                      @update-success="showToastNotification('success')"
                      @update-error="showToastNotification('error')"
                    ></placement-settings-manager>
                  </div>
                </v-card>
              </v-window-item>
              <v-window-item value="models">
                <ModelsList></ModelsList>
              </v-window-item>
              <v-window-item value="help">
                <Help />
              </v-window-item>
            </v-window>
          </template>
        </v-container>

        <!-- Account getting error -->
        <v-container v-else>
          <v-card>
            <v-card-title>Jellybeanss</v-card-title>
            <v-card-text>
              <div>Account config was not found</div>
            </v-card-text>
          </v-card>
        </v-container>
      </v-main>
    </v-layout>

    <!-- Add Widget Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500px">
      <v-card>
        <v-card-title>Add New Widget</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newPlacementId"
            label="Placement ID"
            :error-messages="placementNameError"
            hint="Enter a unique identifier for the placement"
            persistent-hint
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="showAddDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="addNewPlacement"
            :disabled="!newPlacementId || !!placementNameError"
          >
            Add Placement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Toast notifications for placement update/deletion actions -->
    <v-snackbar
      v-model="showToast"
      :timeout="timeout"
      :color="toastColor"
      location="bottom"
      right
    >
      {{ toastMessage }}
      <template #actions>
        <v-btn icon @click="showToast = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-footer app>
      <span>&copy; Jazel {{ new Date().getFullYear() }}</span>
    </v-footer>
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useSettingsStore } from '@/stores/settings'
import PlacementSettingsManager from './components/PlacementSettingsManager.vue'
import Placements from './components/dashboard/Placements.vue'
import ModelsList from './components/ModelsList.vue'
import type { ISettings } from '@/types'
import Help from './components/Help.vue'
import { storeDataInSessionStorage, getDataFromSessionStorage } from './utils'
import { useVehiclesStore } from './stores/vehicles'

const settings = useSettingsStore()
const vehiclesStore = useVehiclesStore()

const currentTab = ref('placements')

const selectedPlacementId = ref<null | string>(null)

const showAddDialog = ref(false)
const newPlacementId = ref('')
const placementNameError = computed(() => {
  if (!newPlacementId.value) return ''
  if (settings.placements.placements[newPlacementId.value]) {
    return 'This placement ID already exists'
  }
  if (!/^[a-z0-9_-]+$/i.test(newPlacementId.value)) {
    return 'Placement ID can only contain letters, numbers, underscores, and hyphens'
  }
  return ''
})
const addNewPlacement = () => {
  const currentSettings = settings.placements
  const newPlacement: ISettings = settings.getDefaultPlacementSettings()
  currentSettings.placements[newPlacementId.value] = newPlacement
  settings.updateSettings(currentSettings)
  showAddDialog.value = false
  newPlacementId.value = ''
}

// Toast notification
const showToast = ref(false)
const toastMessage = ref('')
const toastColor = ref('')
const timeout = ref(3000)
const showToastNotification = (type: 'success' | 'error') => {
  if (type === 'error') {
    toastMessage.value = 'Failed to update settings!'
    toastColor.value = 'error'
    showToast.value = true
  } else {
    toastMessage.value = 'Settings updated successfully!'
    toastColor.value = 'success'
    showToast.value = true
  }
}

const showAccountDialog = ref(false)
const inputAccountId = ref('')
const accountIdError = computed(() => {
  if (!inputAccountId.value) {
    return 'Account ID is required'
  }

  if (!/^[a-z\d_-]+$/i.test(inputAccountId.value)) {
    return 'Account ID can only contain letters, numbers, underscores, and hyphens'
  }

  return ''
})

const setAccountId = async () => {
  if (!inputAccountId.value || accountIdError.value) return

  try {
    settings.accountId = inputAccountId.value
    await settings.fetchSettings(inputAccountId.value)

    // placementsCount.value = Object.keys(settings.placements).length
    // filteredPlacements.value = Object.keys(settings.placements)
    // isInitialized.value = true
    showAccountDialog.value = false
    storeDataInSessionStorage('accountId', inputAccountId.value)
  } catch (error: any) {
    // settings.accountId = ''
    // showSnackbar.value = true
    // snackbarText.value = 'Account ID not found'
    // snackbarColor.value = 'error'
  }
}

onMounted(async () => {
  const accountId = getDataFromSessionStorage('accountId')
  if (accountId) {
    settings.accountId = accountId
    await settings.fetchSettings(settings.accountId)
  } else if (settings.accountId) {
    await settings.fetchSettings(settings.accountId)
  } else {
    showAccountDialog.value = true
  }

  await vehiclesStore.getAvailableMakes()

})
</script>

<style>
.v-select .v-field .v-field__input > input {
  background: none;
  border: none;
  box-shadow: none;
}

:deep(.v-field__input) {
  background: none;
  border: none;
}

:deep(.v-field--variant-outlined .v-field__input) {
  padding: 12px 16px;
}

:deep(.v-field--variant-filled .v-field__input) {
  box-shadow: none;
  line-height: 1.625em;
  padding: 24px 16px 4px 16px;
}
</style>
